import React, { useEffect, useState } from 'react';

import { BottomSheet, Button, Card, Footer, Icon, PageLayout, Typography, CheckCircle } from '@bharatpe/bharat-ui';
import AppHeader from 'components/atoms/AppHeader';
import { SET_MANDATE_COLLECT_DATA, usePendingMandateCollectRequestData, useUpiData } from 'Slices';
import UpiPoweredBy from 'components/atoms/UpiPoweredBy';
import { formatDateDynamic } from 'utils/Utils';
import secureGreen from 'assets/icons/Autopay/secureGreen.svg';
import history from 'utils/History';
import { CollectAuth, forgetUPIPin } from 'utils/nativeUpiHandler';
import Toasty from 'components/atoms/Toasty';
import { ICollectRequestDetails } from 'types';
import { useDispatch } from 'react-redux';
import WrongPinModal from 'components/organisms/Modals/WrongPinModal';
import { getURLParameter } from 'utils/nativeSupport';
import { UPI_ERROR_CODE } from 'utils/CONSTANT';
import { sendEvent } from 'utils/Sentry';
import AlertIcon from 'assets/icons/Autopay/danger.svg';

const CollectAutoPayDetails: React.FC = () => {
	const mandateCollectRequestData = usePendingMandateCollectRequestData();
	const upiData = useUpiData();

	const bankList = upiData?.upiProfileDetails?.user_banks || [];
	const dispatch = useDispatch();
	const [showWrongPinModal, setShowWrongPinModal] = useState<boolean>(false);
	const [showPinChangedSuccessfulModal, setPinChangedSuccessfulModal] = useState(false);

	const { account } = upiData?.upiProfileDetails?.user_banks[0] || [];
	const didEnterWrongPin = getURLParameter(window.location.href, 'wrong-pin');

	useEffect(() => {
		if (didEnterWrongPin === 'true') {
			setShowWrongPinModal(true);
		}
	}, [didEnterWrongPin]);

	const handlePinReset = async () => {
		setShowWrongPinModal(false);
		try {
			const isSuccess = await forgetUPIPin(upiData?.upiProfileDetails?.user_banks[0]);
			if (isSuccess) setPinChangedSuccessfulModal(true);
		} catch (error: any) {
			if (error.errorCode == 'ZM') {
				setShowWrongPinModal(true);
			} else if (error.errorCode == 'Z6') {
				Toasty.error('Exceeded number of pin tries');
			} else if (UPI_ERROR_CODE.includes(error?.errorCode)) {
				Toasty.error(error?.error);
				sendEvent(error);
			} else {
				sendEvent(error);
				Toasty.error('Something went wrong!!');
			}
		}
	};

	const handlePinChnageConfirmationClose = () => {
		setPinChangedSuccessfulModal(false);
	};

	const getAccount = () => {
		return bankList.find(
			(bank: any) =>
				bank?.ifsc == mandateCollectRequestData?.payer_details?.ifsc &&
				bank?.account?.slice(-4) == mandateCollectRequestData?.payer_details?.account_number?.slice(-4),
		);
	};

	const handleContinueClick = async () => {
		try {
			const ApproveItem: ICollectRequestDetails = await CollectAuth(mandateCollectRequestData, '', getAccount(), 'A');
			if (ApproveItem) {
				dispatch(SET_MANDATE_COLLECT_DATA(ApproveItem));
				history.push({ pathname: '/upi/collect-request-status' });
			}
		} catch (err: any) {
			Toasty.error('Something went wrong!!');
		}
	};
	const onBack = () => {
		history.push({ pathname: 'account/upi-settings' });
	};
	return (
		<PageLayout
			className="pb18 white-background"
			footerContent={<Footer primaryActionText="CONTINUE" primaryAction={() => handleContinueClick()} />}>
			<>
				<AppHeader title={''} leftIconAction={onBack} />

				<div className="mx5 mt6 text-center">
					<Typography variant="Bold700" className="text-center mt4 width-100" component="span">
						Set auto-pay
					</Typography>
					<div className="flex flex-row flex-justify-center flex-center">
						<img src={secureGreen} loading="lazy" className="mt2 mr1" height={20} />
						<Typography variant="Regular200" className="mt2 text-secure" component="span">
							SEAMLESS & SECURE
						</Typography>
					</div>
					<Card className="p4 mb5 mt5 flex flex-row content-center">
						<img src={mandateCollectRequestData?.payee_details?.payee_logo} height={48} width={78} className="mr3" />
						<div className="flex flex-column flex-start mt2">
							<Typography variant="Regular300">{mandateCollectRequestData?.payee_details?.payee_name}</Typography>
							<Typography variant="Light100" className="grey">
								{mandateCollectRequestData?.payee_details?.payee_vpa}
							</Typography>
						</div>
					</Card>
					<Card className="p4 mb5 mt5 flex flex-row flex-start flex-justify">
						<Typography variant="Bold400">Amount</Typography>
						<div className="flex flex-column">
							<div>
								<Typography variant="Light200" className="mr2" component="span">
									UP TO
								</Typography>
								<Typography className="Bold700" component="span">
									₹{mandateCollectRequestData?.amount}
								</Typography>
							</div>
							<div className="auto-debit">
								<Typography component="span">AUTO DEBITED</Typography>
							</div>
						</div>
					</Card>
					<Card className="p4 mb5 mt5 flex flex-column">
						<div className="flex flex-column flex-start mb3">
							<Typography variant="Light100" className="grey-dark">
								FREQUENCY
							</Typography>
							<Typography variant="Regular300 mt2">As Presented</Typography>
						</div>
						<div className="flex flex-column flex-start mb3">
							<Typography variant="Light100" className="grey-dark">
								AUTOPAY VALIDITY
							</Typography>
							<Typography variant="Regular300 mt2">
								<>{formatDateDynamic(mandateCollectRequestData?.expire_after, `DD MMM' YYYY  h:mm a`)}</>
							</Typography>
						</div>
						<div className="horizontal-dashed-divider bg-white mb3"></div>
						<div className="flex flex-column flex-start mb3">
							<Typography variant="Light200" className="grey">
								UAN
							</Typography>
							<Typography variant="Regular300 mt2">{mandateCollectRequestData?.bank_rrn}</Typography>
						</div>
						<div className="flex flex-column flex-start mb3">
							<Typography variant="Light200" className="grey">
								Remarks
							</Typography>
							<Typography variant="Regular300 mt2">{mandateCollectRequestData?.remarks}</Typography>
						</div>
						<div className="horizontal-dashed-divider bg-white mb3"></div>
						<div className="flex flex-column flex-start mb3">
							<Typography variant="Light200" className="grey">
								Debit from
							</Typography>
							<div className="flex flex-row flex-center ">
								<img
									src={mandateCollectRequestData?.payer_details?.bank_logo}
									loading="lazy"
									className="mt2 mr1"
									height={20}
								/>
								<Typography className="Regular300 mt2">
									{mandateCollectRequestData?.payer_details?.bank_name} **** ****{' '}
									{mandateCollectRequestData?.payer_details?.account_number?.slice(-4)}
								</Typography>
							</div>
						</div>
					</Card>
					<div className="content-center">
						<UpiPoweredBy />
					</div>
				</div>
				{showWrongPinModal && (
					<WrongPinModal
						handleClickPrimary={handlePinReset}
						handleClickSecondary={() => handleContinueClick('WRONG_PIN_MODAL')}
						handleModalClose={() => setShowWrongPinModal(false)}
						title={'Wrong UPI PIN entered'}
						description={
							'Please use the correct PIN and try again. your bank will temporarily block UPI payments, if you enter the wrong PIN too many times.'
						}
						primaryButtonName={'Reset PIN'}
						secondaryButtonName={'Retry'}
						icon={AlertIcon}
						type={'info'}
					/>
				)}
				<BottomSheet visible={showPinChangedSuccessfulModal} showCloseButton={false} rounded>
					<div className="p4 flex flex-column">
						<Icon icon={CheckCircle} className="mr2 success-logo" width={40} height={40} />
						<Typography variant="Bold500" className="mt2">
							PIN Successfully Changed
						</Typography>
						<Typography variant="Regular200" className="grey mt1 mb4">
							You have successfully updated your UPI PIN for{' '}
							{upiData?.upiProfileDetails?.user_banks[0]?.['acc-provider-name']} account {account}
						</Typography>
						<Button onClick={handlePinChnageConfirmationClose} className="mt4">
							Done
						</Button>
					</div>
				</BottomSheet>
			</>
		</PageLayout>
	);
};
export default CollectAutoPayDetails;
